import React, { useState } from 'react';
import { grid } from '@noths/polaris-client-ribbons-base';
import type { ProductCardProps } from '@noths/polaris-client-ribbons-design-system';
import { ProductCard } from '@noths/polaris-client-ribbons-design-system';
import {
  addParamsToHref,
  getDeliveryByEventLabel,
  getProductIdFromProductCode,
  isBrowser,
} from '@noths/polaris-client-utils';

import type { ProductListProps } from 'src/components/organisms/ProductList/types';
import { PRODUCT_CARD_GRID_ITEM_ID } from 'src/components/organisms/ProductPages/constants/elementIds';
import type { BrowseDataAPIProduct } from 'src/services/browse-data-api/types/BrowseDataAPIProduct';
import { PRODUCT_ID_DATA_ATTRIBUTE } from 'src/utils/scrollProductCardIntoView';
import * as styles from './ProductList.styles';

const INITIAL_NUMBER_OF_PRODUCTS_TO_DISABLE_LAZY_LOADING = 6;
export const PRODUCT_CARD_GRID_ITEM_TEST_ID = 'PRODUCT_CARD_GRID_ITEM_TEST_ID';

/**
 * Currently responsible for rendering:
 *  - lists of products
 *  - lists of expandedProducts (if expanded search scenario)
 *  - expanded search messaging
 *
 * Should be responsible for rendering:
 *  - a list of products (agnostic about whether it's a full page, exact results, expanded search results)
 * @component
 */
export const ProductList = ({
  expandedProducts,
  expandedResultsMessage,
  favouriteProductCodes = [],
  onProductClick,
  onProductVisible,
  page,
  products,
  referredBy,
  providerAttributionToken,
  updateFavourite,
  updatePageHistory,
}: ProductListProps) => {
  const [fadeInProductList] = useState(isBrowser());

  const handleProductClick = (
    href: string,
    productCode: number,
    linkOpensInSameWindow: boolean,
  ) => {
    const hrefWithTrackingParams = addParamsToHref(href, {
      ...(providerAttributionToken && { attributionToken: providerAttributionToken }),
      ...(referredBy && { referredBy }),
    });

    if (linkOpensInSameWindow) {
      updatePageHistory({ page, productCode });
    }

    onProductClick({
      productUrl: hrefWithTrackingParams,
      destinationUrl: href,
      productCode,
      linkOpensInSameWindow,
    });
  };

  const handleProductVisible: ProductCardProps['onVisible'] = ({ code }) => {
    const productId = getProductIdFromProductCode(code);

    onProductVisible(productId);
  };

  const renderProducts = (products: BrowseDataAPIProduct[]) => {
    return products.map((product, index) => {
      const productHref = product.links[0]?.rel || '';
      const secondaryLabelText = product.deliveryByEvent?.arriveByEvent
        ? getDeliveryByEventLabel(product.deliveryByEvent.eventName)
        : undefined;

      return (
        <div
          key={`${product.code}`}
          css={styles.productCardGridItem}
          id={`${PRODUCT_CARD_GRID_ITEM_ID}-${index}`}
          data-testid={PRODUCT_CARD_GRID_ITEM_TEST_ID}
          {...{ [PRODUCT_ID_DATA_ATTRIBUTE]: getProductIdFromProductCode(product.code) }}
        >
          <ProductCard
            {...product}
            isFavourited={favouriteProductCodes.includes(String(product.code))}
            disableImageLazyLoading={index < INITIAL_NUMBER_OF_PRODUCTS_TO_DISABLE_LAZY_LOADING}
            href={productHref}
            onProductClick={handleProductClick}
            onVisible={handleProductVisible}
            productIndex={index}
            secondaryLabelText={secondaryLabelText}
            updateFavourite={updateFavourite}
          />
        </div>
      );
    });
  };

  return (
    <div css={[grid, styles.productListWrapper, fadeInProductList && styles.fadeIn]}>
      {products && renderProducts(products)}
      {expandedResultsMessage && expandedResultsMessage}
      {expandedProducts && renderProducts(expandedProducts)}
    </div>
  );
};
